import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { milestoneService } from '../services/api';

const MILESTONE_COLORS = [
  { label: 'Violet', value: '#9c27b0' },
  { label: 'Rouge', value: '#f44336' },
  { label: 'Orange', value: '#ff9800' },
  { label: 'Bleu', value: '#2196f3' },
  { label: 'Vert', value: '#4caf50' },
  { label: 'Noir', value: '#212121' },
];

const MilestoneForm = ({ open, onClose, projectId, milestone, onSaved, onDeleted }) => {
  const isEdit = Boolean(milestone);
  const [form, setForm] = useState({ title: '', due_date: '', description: '', status: 'pending', color: '#9c27b0' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (milestone) {
      setForm({
        title: milestone.title || '',
        due_date: milestone.due_date ? milestone.due_date.split('T')[0] : '',
        description: milestone.description || '',
        status: milestone.status || 'pending',
        color: milestone.color || '#9c27b0'
      });
    } else {
      setForm({ title: '', due_date: '', description: '', status: 'pending', color: '#9c27b0' });
    }
    setError('');
  }, [milestone, open]);

  const handleChange = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.due_date) {
      setError('Titre et date requis');
      return;
    }
    setLoading(true);
    try {
      let result;
      if (isEdit) {
        result = await milestoneService.update(milestone.id, form);
        onSaved(result.data.milestone);
      } else {
        result = await milestoneService.create(projectId, form);
        onSaved(result.data.milestone);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!milestone) return;
    setLoading(true);
    try {
      await milestoneService.delete(milestone.id);
      onDeleted(milestone.id);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Modifier le jalon' : 'Nouveau jalon'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Titre *"
            value={form.title}
            onChange={handleChange('title')}
            fullWidth
            autoFocus
          />
          <TextField
            label="Date *"
            type="date"
            value={form.due_date}
            onChange={handleChange('due_date')}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={handleChange('description')}
            fullWidth
            multiline
            rows={2}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Statut</InputLabel>
              <Select value={form.status} onChange={handleChange('status')} label="Statut">
                <MenuItem value="pending">En attente</MenuItem>
                <MenuItem value="completed">Atteint</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Couleur</InputLabel>
              <Select value={form.color} onChange={handleChange('color')} label="Couleur"
                renderValue={(val) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: val }} />
                    {MILESTONE_COLORS.find(c => c.value === val)?.label || val}
                  </Box>
                )}
              >
                {MILESTONE_COLORS.map(c => (
                  <MenuItem key={c.value} value={c.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: c.value }} />
                      {c.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {error && <Box sx={{ color: 'error.main', fontSize: '0.875rem' }}>{error}</Box>}
        </Box>
      </DialogContent>
      <DialogActions>
        {isEdit && (
          <Button color="error" onClick={handleDelete} disabled={loading} sx={{ mr: 'auto' }}>
            Supprimer
          </Button>
        )}
        <Button onClick={onClose} disabled={loading}>Annuler</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {isEdit ? 'Enregistrer' : 'Créer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MilestoneForm;
